/* eslint-disable sonarjs/no-duplicate-string */
import {
  ErrorHandling,
  EVENT_APP_FATAL_ERROR,
  EVENT_APP_LOADING_COMPLETE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  FatalErrors,
  isUnsupportedDesktopSafari,
  isUnsupportedMobileSafari,
  Logger,
} from "@classdojo/monsterverse-utils";
import { translate } from "@monsterverse/i18n";
import { getOrCreateRootDiv, Modal } from "@monsterverse/ui";
import { StyledEngineProvider } from "@mui/material/styles";
import { type ComponentChildren, Fragment, type JSX, render } from "preact";

import { setupTranslations } from "../AWSHostURLs";
import { postToApp } from "../MessageHandler";
import type { PCAppEventHandler } from "../PCAppEventHandler";
import { getLauncherLatestURL } from "./getLauncherLatestURL";

type ErrorModalProps = {
  title?: string;
  description?: string | JSX.Element[];
  yesText?: string;
  onYesClicked?: () => void;
  noText?: string;
  onNoClicked?: () => void;
  children?: ComponentChildren;
};

const FatalErrorModal: React.FC<{ props: ErrorModalProps }> = ({ props }) => {
  return (
    <Modal
      shown={true}
      titleText={props.title ?? translate("common.errors.defaults.title")}
      descriptionText={props.description}
      onYes={
        props.onYesClicked
          ? { text: props.yesText ?? translate("common.errors.defaults.ok"), onClicked: props.onYesClicked }
          : undefined
      }
      onNo={
        props.onNoClicked
          ? { text: props.noText ?? translate("common.errors.defaults.cancel"), onClicked: props.onNoClicked }
          : undefined
      }
      children={props.children}
    />
  );
};

let fatalErrorTriggered = false;
function renderFatalErrorModal(errorProps: ErrorModalProps) {
  if (fatalErrorTriggered) {
    return;
  }
  fatalErrorTriggered = true;

  render(
    <StyledEngineProvider injectFirst>
      <FatalErrorModal props={errorProps} />
    </StyledEngineProvider>,
    getOrCreateRootDiv()
  );
}

function webGLFatalError() {
  Logger.logInfo("FatalErrorModal webGLFatalError");

  renderFatalErrorModal({
    title: translate("common.errors.webgl_support.title"),
    description: translate("common.errors.webgl_support.description"),
    yesText: translate("common.errors.webgl_support.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGLFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2FatalError() {
  Logger.logInfo("FatalErrorModal webGL2FatalError");

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.default.title"),
    description: translate("common.errors.webgl2_support.default.description"),
    yesText: translate("common.errors.webgl2_support.default.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2FatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2iOSFatalError() {
  Logger.logInfo("FatalErrorModal webGL2iOSFatalError");

  const errorDescription = () => {
    return (
      <>
        <Fragment>
          {translate("common.errors.webgl2_support.ios.description")}
          <br />
          <br />
          <a href="https://support.apple.com/en-us/HT204204">
            {translate("common.errors.webgl2_support.ios.link_text")}
          </a>
        </Fragment>
      </>
    );
  };

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.ios.title"),
    description: [errorDescription()],
    yesText: translate("common.errors.webgl2_support.ios.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2MacOSFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function webGL2MacOSFatalError() {
  Logger.logInfo("FatalErrorModal webGL2MacOSFatalError");

  const errorDescription = () => {
    return (
      <>
        <Fragment>
          {translate("common.errors.webgl2_support.macos.description")}
          <br />
          <br />
          <a href="https://support.apple.com/en-us/HT201541">
            {translate("common.errors.webgl2_support.macos.link_text")}
          </a>
        </Fragment>
      </>
    );
  };

  renderFatalErrorModal({
    title: translate("common.errors.webgl2_support.macos.title"),
    description: [errorDescription()],
    yesText: translate("common.errors.webgl2_support.macos.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "webGL2MacOSFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function loggedOutUserFatalError() {
  Logger.logInfo("FatalErrorModal loggedOutUserFatalError");

  renderFatalErrorModal({
    title: translate("common.errors.not_logged_in.title"),
    description: translate("common.errors.not_logged_in.description"),
    yesText: translate("common.errors.not_logged_in.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "LoggedOutUserFatalError" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function timeoutFromServerError(error: FatalErrors) {
  disconnectedFromServerError(error, { isTimeout: true });
}

function timeoutFromServerTicketExpiredError(error: FatalErrors) {
  disconnectedFromServerError(error, { isTimeout: true });
}

/**
 * Handles disconnection errors from the server by showing an error modal.
 * Increments different metrics based on the error type:
 * - For idle timeouts: "monsterverse.stats.errors.idleTimeout"
 * - For ticket expired timeouts: "monsterverse.stats.errors.ticketExpiredTimeout"
 * - For unknown timeouts: "monsterverse.stats.errors.unknownTimeout"
 * - Always increments "monsterverse.stats.errors.totalTimeouts"
 * - On user-selected exit in the modal: "monsterverse.stats.errors.totalExitsFromTimeouts"
 *
 * @param error The fatal error type that caused the disconnection
 * @param isTimeout Whether this was triggered by a timeout vs other disconnection
 */
function disconnectedFromServerError(
  error: FatalErrors,
  options: {
    isTimeout?: boolean;
  } = {}
) {
  const { isTimeout = false } = options;
  if (isTimeout) {
    if (error === FatalErrors.ServerIdleTimeout) {
      Logger.incrementMetric("monsterverse.stats.errors.idleTimeout");
    } else if (error === FatalErrors.ServerTicketExpiredTimeout) {
      Logger.incrementMetric("monsterverse.stats.errors.ticketExpiredTimeout");
    } else {
      // NOTE: 4/21/2025 This is added as a catch-all to detect any other server timeout errors that are unexpected.
      Logger.incrementMetric("monsterverse.stats.errors.unknownTimeout");
    }
  } else {
    Logger.logInfo(`FatalErrorModal disconnectedFromServerError ${FatalErrors[error]}`, {}, { logOnce: true });
  }

  // This will increment the total number of timeouts regardless of whether it was a timeout or other disconnection.
  Logger.incrementMetric("monsterverse.stats.errors.totalTimeouts");

  const idleLabels = {
    title: translate("common.errors.idle.title"),
    yesText: translate("common.errors.idle.yes"),
    noText: translate("common.errors.idle.no"),
  };
  const otherLabels = {
    title: translate("common.errors.other.title"),
    yesText: translate("common.errors.other.yes"),
    noText: translate("common.errors.other.no"),
  };
  const labels = error === FatalErrors.ServerIdleTimeout ? idleLabels : otherLabels;

  renderFatalErrorModal({
    ...labels,
    onYesClicked: () => {
      const url = getLauncherLatestURL();
      if (url) {
        window.location.replace(url);
      } else {
        window.location.reload();
      }
    },
    onNoClicked: () => {
      // This will only increment if the user opted to exit the app in the modal.
      Logger.incrementMetric("monsterverse.stats.errors.totalExitsFromTimeouts");
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
  });
}

function removePlayer() {
  Logger.logInfo(`FatalErrorModal removePlayer`);

  renderFatalErrorModal({
    title: translate("common.errors.removed.title"),
    yesText: translate("common.errors.removed.yes"),
    onYesClicked: () => {
      Logger.logInfo("Returning user to app", { reason: "RemovePlayer" });
      Logger.flushLogs();
      postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
    },
    children: (
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <img
          src={"https://clients.multiplayer.classdojo.com/assets/f/e8185338d7008bf2d9d30f97ed3e0dea/removed.png"}
          style={{ width: "100px" }}
        />
        <div style={{ fontSize: "16px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
          {translate("common.errors.removed.description")}
        </div>
      </div>
    ),
  });
}

function incompatibleBrowserError() {
  Logger.logInfo("FatalErrorModal incompatibleBrowserError");

  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.incompatible_browser.title"),
      description: translate("common.errors.incompatible_browser.description"),
      noText: translate("common.errors.incompatible_browser.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "IncompatibleBrowser" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function bundleNotFoundError() {
  Logger.logInfo("FatalErrorModal bundleNotFoundError");

  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.bundle_not_found.title"),
      description: translate("common.errors.bundle_not_found.description"),
      noText: translate("common.errors.bundle_not_found.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "BundleNotFoundError" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function bundleLoadError() {
  Logger.logInfo("FatalErrorModal bundleLoadError");

  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.bundle_load_error.title"),
      description: translate("common.errors.bundle_load_error.description"),
      noText: translate("common.errors.bundle_load_error.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "BundleLoadError" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
    });
  });
}

function outageError() {
  Logger.logInfo("FatalErrorModal outage");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.outage.title"),
      noText: translate("common.errors.outage.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "Outage" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "100px" }}
          />
          <div style={{ fontSize: "16px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.outage.description")}
          </div>
        </div>
      ),
    });
  });
}

function webGLContextFailedToInitializeError() {
  Logger.logInfo("FatalErrorModal webGLContextFailedToInitializeError");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.webgl_context_failed_init.title"),
      noText: translate("common.errors.webgl_context_failed_init.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "WebGLFailedToInitialize" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "200px" }}
          />
          <div style={{ fontSize: "20px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.webgl_context_failed_init.description")}
          </div>
        </div>
      ),
    });
  });
}

function incompatibleIOSVersionError() {
  Logger.logInfo("FatalErrorModal incompatibleIOSVersionError");

  //Ensure translations are loaded
  setupTranslations().finally(() => {
    renderFatalErrorModal({
      title: translate("common.errors.incompatible_os.title"),
      noText: translate("common.errors.incompatible_os.no"),
      onNoClicked: () => {
        Logger.logInfo("Returning user to app", { reason: "IncompatibleIOSVersion" });
        Logger.flushLogs();
        postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
      },
      children: (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <img
            src={"https://clients.multiplayer.classdojo.com/assets/f/f67dfbe7808158abeed8462b47aa92d3/outage.png"}
            style={{ width: "200px" }}
          />
          <div style={{ fontSize: "20px", marginLeft: "20px", color: "white", whiteSpace: "pre-wrap" }}>
            {translate("common.errors.incompatible_os.description")}
          </div>
        </div>
      ),
    });
  });
}

function assertImpossible(_p: never): never {
  throw new Error("INCONCEIVABLE!");
}

export function fatalErrorHandler(
  iframeEl: HTMLIFrameElement,
  pcAppEventHandler: PCAppEventHandler,
  error: FatalErrors
) {
  iframeEl.remove();
  pcAppEventHandler.post({ eventName: EVENT_APP_LOADING_COMPLETE });

  switch (error) {
    case FatalErrors.UserLoggedOut:
      loggedOutUserFatalError();
      break;
    case FatalErrors.NoWebGL2:
      if (isUnsupportedMobileSafari()) {
        webGL2iOSFatalError();
        break;
      }
      if (isUnsupportedDesktopSafari()) {
        webGL2MacOSFatalError();
        break;
      }
      webGL2FatalError();
      break;
    case FatalErrors.NoWebGL:
      webGLFatalError();
      break;
    case FatalErrors.IncompatibleBrowser:
      incompatibleBrowserError();
      break;
    case FatalErrors.ServerIdleTimeout:
      timeoutFromServerError(error);
      break;
    case FatalErrors.ServerTicketExpiredTimeout:
      timeoutFromServerTicketExpiredError(error);
      break;
    case FatalErrors.ServerReconnectTimeout:
    case FatalErrors.ServerShuttingDown:
    case FatalErrors.WebsocketClosure:
      disconnectedFromServerError(error);
      break;
    case FatalErrors.RemovePlayer:
      removePlayer();
      break;
    case FatalErrors.Outage:
      outageError();
      break;
    case FatalErrors.BundleNotFound:
      bundleNotFoundError();
      break;
    case FatalErrors.BundleLoadError:
      bundleLoadError();
      break;
    case FatalErrors.IOS17ContextLoss:
      incompatibleIOSVersionError();
      break;
    case FatalErrors.WebGLContextFailedToInitialize:
      webGLContextFailedToInitializeError();
      break;
    default:
      assertImpossible(error);
  }

  // NOTE: Removed ServerIdleTimeout and ServerTicketExpiredTimeout because they log too often
  // and we're not monitoring this. ServerIdleTimeout is now logged as a metric.
  if (error !== FatalErrors.ServerIdleTimeout && error !== FatalErrors.ServerTicketExpiredTimeout) {
    Logger.logInfo(`Encountered fatal error: ${FatalErrors[error]}`);
  }

  // Final step: flush logs to ensure all metrics are sent.
  Logger.flushLogs();
}

export function handleFatalErrors(iframeEl: HTMLIFrameElement, pcAppEventHandler: PCAppEventHandler) {
  pcAppEventHandler.on(EVENT_APP_FATAL_ERROR, (data: unknown) => {
    fatalErrorHandler(iframeEl, pcAppEventHandler, data as FatalErrors);
  });

  const fatalError = (error: FatalErrors) => {
    pcAppEventHandler.post({ eventName: EVENT_APP_FATAL_ERROR, eventValues: [error] });
  };

  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.UserLoggedOut, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.IncompatibleBrowser, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerIdleTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerReconnectTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerTicketExpiredTimeout, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.ServerShuttingDown, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.RemovePlayer, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebsocketClosure, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.Outage, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.BundleNotFound, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.BundleLoadError, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.IOS17ContextLoss, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.NoWebGL2, fatalError);
  ErrorHandling.instance.registerFatalErrorHandling(FatalErrors.WebGLContextFailedToInitialize, fatalError);
}
